import type { Vec3 } from '@indusketch/geometry';
import { buildingInteriorArea } from '../building.js';
import { columnSpan } from '../columns.js';
import { mezzanineArea } from '../mezzanine.js';
import { storeyArea } from '../office.js';
import { sectionMass, STEEL_DENSITY, type SectionId } from '../steelProfiles.js';
import type { Building, OpeningType, ProjectDocument, Wall } from '../types.js';
import type { PriceBase } from '../priceCatalogs.js';
import { concreteQuantities, concreteWall, precastVolume, type ConcretePart } from './concrete.js';
import { hallSteel, type SteelMemberKind } from './hallStructure.js';
import { roofParts } from './roof.js';
import { wallSolidParts } from './walls.js';
import type { ReportTable } from './report.js';
import { formatNumber } from '../units.js';
import { fillText, modelTexts } from '../texts.js';

export type CostUnit = 't' | 'm2' | 'm3' | 'pcs' | 'kWp';

/** Peak power of roof photovoltaics per square metre of covered roof. */
export const PV_KWP_PER_M2 = 0.2;

/**
 * Items of the rough cost estimate with rough German unit prices (2026, net, assembled).
 * Only orientation for an early budget; every price is set per project.
 */
export const COST_ITEMS = [
  { id: 'steel', unit: 't', price: 3200 },
  // Reinforced concrete cast in place: concrete, reinforcement and formwork apart.
  { id: 'concrete', unit: 'm3', price: 200 },
  { id: 'rebar', unit: 't', price: 2100 },
  { id: 'formwork', unit: 'm2', price: 55 },
  { id: 'precast', unit: 'm3', price: 1300 },
  { id: 'roofSandwich', unit: 'm2', price: 65 },
  { id: 'roofFlat', unit: 'm2', price: 80 },
  { id: 'wallSandwich', unit: 'm2', price: 55 },
  { id: 'wallTrapezoid', unit: 'm2', price: 35 },
  { id: 'wallMasonry', unit: 'm2', price: 95 },
  { id: 'wallBrick', unit: 'm2', price: 140 },
  { id: 'wallDrywall', unit: 'm2', price: 65 },
  { id: 'hallFloor', unit: 'm2', price: 85 },
  { id: 'mezzanine', unit: 'm2', price: 280 },
  { id: 'gate', unit: 'pcs', price: 4500 },
  { id: 'fireGate', unit: 'pcs', price: 9000 },
  { id: 'rapidDoor', unit: 'pcs', price: 8000 },
  { id: 'dock', unit: 'pcs', price: 14000 },
  { id: 'door', unit: 'pcs', price: 1200 },
  { id: 'doubleDoor', unit: 'pcs', price: 2200 },
  { id: 'window', unit: 'm2', price: 450 },
  { id: 'skylight', unit: 'pcs', price: 1500 },
  { id: 'skylightBand', unit: 'm2', price: 350 },
  { id: 'smokeVent', unit: 'pcs', price: 2500 },
  { id: 'pv', unit: 'kWp', price: 850 },
] as const satisfies readonly { id: string; unit: CostUnit; price: number }[];

export type CostItemId = (typeof COST_ITEMS)[number]['id'];

/** Former items now measured as concrete, reinforcement and formwork; own prices drop out. */
const REPLACED_ITEMS = ['slab', 'wallConcrete', 'concreteColumn'];

export const COST_SURCHARGES = ['foundations', 'services', 'fees'] as const;
export type CostSurchargeId = (typeof COST_SURCHARGES)[number];

/** Connections, plates and bolts on top of the members' weight. */
export const DEFAULT_STEEL_EXTRA = 10;
export const DEFAULT_COST_RANGE = 15;
/** Haunches are counted as a plate of this thickness over their triangle. */
const HAUNCH_PLATE = 0.02;

export interface SteelTonnage {
  /** Columns, rafters, beams and haunches. */
  readonly frame: number;
  /** Purlins and girts. */
  readonly secondary: number;
  /** Struts and bracing. */
  readonly bracing: number;
  /** Columns set by hand. */
  readonly columns: number;
  /** Allowance for connections. */
  readonly extra: number;
  readonly total: number;
}

export interface CostLine {
  readonly id: CostItemId;
  readonly unit: CostUnit;
  readonly quantity: number;
  readonly price: number;
  readonly total: number;
}

export interface CostEstimate {
  /** Items with a quantity, in catalogue order. */
  readonly lines: readonly CostLine[];
  readonly steel: SteelTonnage;
  readonly measured: number;
  readonly surcharges: readonly {
    readonly id: CostSurchargeId;
    readonly percent: number;
    readonly amount: number;
    /** Measured from the plan instead (foundations from the columns). */
    readonly measured: boolean;
  }[];
  readonly total: number;
  readonly low: number;
  readonly high: number;
  readonly range: number;
}

const FRAME: readonly SteelMemberKind[] = [
  'column',
  'rafter',
  'interiorColumn',
  'gableColumn',
  'beam',
];
const SECONDARY: readonly SteelMemberKind[] = ['purlin', 'girt'];

const tonnes = (kg: number) => kg / 1000;
const len3 = (a: Vec3, b: Vec3) => Math.hypot(b.x - a.x, b.y - a.y, b.z - a.z);
function triangleArea(a: Vec3, b: Vec3, c: Vec3): number {
  const u = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
  const v = { x: c.x - a.x, y: c.y - a.y, z: c.z - a.z };
  return Math.hypot(u.y * v.z - u.z * v.y, u.z * v.x - u.x * v.z, u.x * v.y - u.y * v.x) / 2;
}
/** Area of a roof face: its triangles, or a fan over its corners. */
function faceArea(corners: readonly Vec3[], triangles?: readonly number[]): number {
  let sum = 0;
  if (triangles)
    for (let i = 0; i + 2 < triangles.length; i += 3) {
      const [a, b, c] = [triangles[i], triangles[i + 1], triangles[i + 2]].map(
        (k) => corners[k ?? 0],
      );
      if (a && b && c) sum += triangleArea(a, b, c);
    }
  else
    for (let i = 1; i + 1 < corners.length; i++) {
      const [a, b, c] = [corners[0], corners[i], corners[i + 1]];
      if (a && b && c) sum += triangleArea(a, b, c);
    }
  return sum;
}

/** Steel weight of the halls' frames and the steel columns set by hand. */
export function steelTonnage(doc: ProjectDocument, base?: PriceBase): SteelTonnage {
  let frame = 0;
  let secondary = 0;
  let bracing = 0;
  for (const b of Object.values(doc.buildings)) {
    const steel = hallSteel(doc, b);
    if (!steel) continue;
    for (const m of steel.members) {
      const kg = len3(m.a, m.b) * sectionMass(m.profile);
      if (FRAME.includes(m.kind)) frame += kg;
      else if (SECONDARY.includes(m.kind)) secondary += kg;
      else bracing += kg;
    }
    for (const p of steel.plates)
      frame += triangleArea(...p.corners) * HAUNCH_PLATE * STEEL_DENSITY;
  }
  // A steel column set by hand weighs like an HEB of its width.
  const heb = (width: number): SectionId =>
    width <= 0.21 ? 'HEB 200' : width <= 0.25 ? 'HEB 240' : width <= 0.29 ? 'HEB 280' : 'HEB 300';
  const columns = Object.values(doc.columns)
    .filter((c) => c.material === 'steel')
    .reduce((kg, c) => {
      const span = columnSpan(doc, c);
      return kg + (span.top - span.base) * sectionMass(heb(c.width));
    }, 0);
  const net = tonnes(frame + secondary + bracing + columns);
  const extra = (net * surchargePercent(doc, 'steelExtra', base)) / 100;
  return {
    frame: tonnes(frame),
    secondary: tonnes(secondary),
    bracing: tonnes(bracing),
    columns: tonnes(columns),
    extra,
    total: net + extra,
  };
}

const FACADE_ITEM: Record<Building['facade'], CostItemId> = {
  'iso-vertical': 'wallSandwich',
  'iso-horizontal': 'wallSandwich',
  trapezoid: 'wallTrapezoid',
  smooth: 'wallMasonry',
  brick: 'wallBrick',
};
/** Concrete walls are measured with the reinforced concrete (see `concreteQuantities`). */
const CLADDING_ITEM: Record<Wall['cladding'], CostItemId | null> = {
  plain: 'wallDrywall',
  sandwich: 'wallSandwich',
  'sandwich-horizontal': 'wallSandwich',
  'sand-lime': 'wallMasonry',
  concrete: null,
  brick: 'wallBrick',
};
const OPENING_ITEM: Partial<Record<OpeningType, CostItemId>> = {
  'sectional-door': 'gate',
  'rolling-door': 'gate',
  'rapid-door': 'rapidDoor',
  'fire-gate': 'fireGate',
  'loading-dock': 'dock',
  door: 'door',
  'glass-door': 'door',
  'double-door': 'doubleDoor',
  'glass-double-door': 'doubleDoor',
};

/** Quantities of every cost item measured from the plan (t, m² or pieces). */
export function costQuantities(doc: ProjectDocument, base?: PriceBase): Record<CostItemId, number> {
  const q = Object.fromEntries(COST_ITEMS.map((i) => [i.id, 0])) as Record<CostItemId, number>;
  const add = (id: CostItemId, amount: number) => {
    q[id] += amount;
  };
  q.steel = steelTonnage(doc, base).total;
  const concrete = concreteQuantities(doc);
  q.concrete = concrete.volume;
  q.rebar = concrete.rebar;
  q.formwork = concrete.formwork;
  q.precast = precastVolume(doc);

  // Walls without their doors, gates, windows and open parts.
  for (const w of Object.values(doc.walls)) {
    const owner = w.ownerId ? doc.buildings[w.ownerId] : undefined;
    const item =
      owner && w.kind === 'exterior' ? FACADE_ITEM[owner.facade] : CLADDING_ITEM[w.cladding];
    if (!item || concreteWall(doc, w)) continue;
    const area = wallSolidParts(doc, w).reduce(
      (s, p) => s + (p.to - p.from) * (p.top - p.bottom),
      0,
    );
    add(item, area);
  }

  for (const b of Object.values(doc.buildings)) {
    const ownRoof = b.office?.placement !== 'indoor';
    if (ownRoof) {
      const parts = roofParts(b);
      // Gables and the sides of a mono-pitch roof above the eaves.
      for (const g of parts.gables) add(FACADE_ITEM[b.facade], triangleArea(...g.corners));
      const area = parts.planes.reduce((s, p) => s + faceArea(p.corners, p.triangles), 0);
      add(parts.flat ? 'roofFlat' : 'roofSandwich', area);
      if (b.pv) add('pv', area * b.pv.share * PV_KWP_PER_M2);
    }
    if (b.office) {
      if (b.office.placement !== 'indoor') add('hallFloor', storeyArea(b, 0));
    } else if (b.kind === 'hall') add('hallFloor', buildingInteriorArea(b));
  }

  for (const m of Object.values(doc.mezzanines)) add('mezzanine', mezzanineArea(m));

  for (const o of Object.values(doc.openings)) {
    const item = OPENING_ITEM[o.type];
    if (item) add(item, 1);
    else if (o.type === 'window' || o.type === 'window-band') add('window', o.width * o.height);
  }

  for (const r of Object.values(doc.roofElements)) {
    const area = r.width * r.depth;
    if (r.type === 'skylight-band') add('skylightBand', area);
    else add(r.type === 'smoke-vent' ? 'smokeVent' : 'skylight', 1);
    // The roof covering is left out where the element sits.
    const b = doc.buildings[r.buildingId];
    if (b) add(roofParts(b).flat ? 'roofFlat' : 'roofSandwich', -area);
  }
  q.roofFlat = Math.max(0, q.roofFlat);
  q.roofSandwich = Math.max(0, q.roofSandwich);
  return q;
}

/** Where a unit price comes from: set in the project, from the catalog, or the guide price. */
export type PriceSource = 'project' | 'catalog' | 'guide';

export function priceSource(doc: ProjectDocument, id: CostItemId, base?: PriceBase): PriceSource {
  if (doc.settings.costs?.prices?.[id] !== undefined) return 'project';
  return base?.prices[id] !== undefined ? 'catalog' : 'guide';
}

/** Unit price of an item: the project's own, else the catalog's, else the guide price. */
export const costPrice = (doc: ProjectDocument, id: CostItemId, base?: PriceBase): number =>
  doc.settings.costs?.prices?.[id] ??
  base?.prices[id] ??
  COST_ITEMS.find((i) => i.id === id)?.price ??
  0;

/** A percent of the estimate: the project's own, else the catalog's, else the usual one. */
export function surchargePercent(
  doc: ProjectDocument,
  id: CostSurchargeId | 'steelExtra' | 'range',
  base?: PriceBase,
): number {
  const usual = id === 'steelExtra' ? DEFAULT_STEEL_EXTRA : id === 'range' ? DEFAULT_COST_RANGE : 0;
  return doc.settings.costs?.[id] ?? base?.surcharges?.[id] ?? usual;
}

/** Euro amount without cents, in the current number format. */
export const formatEuro = (value: number): string => `${formatNumber(Math.round(value), 0)} €`;

/** Quantity with its unit: tonnes, m³ and kWp with one decimal, areas and pieces whole. */
export function formatCostQuantity(quantity: number, unit: CostUnit): string {
  const t = modelTexts().costs;
  const digits = unit === 't' || unit === 'm3' || unit === 'kWp' ? 1 : 0;
  return `${formatNumber(quantity, digits, digits)} ${t.units[unit]}`;
}

/** The estimate as a report table with its notes (for the PDF report). */
export function costReportTable(
  doc: ProjectDocument,
  base?: PriceBase,
): { table: ReportTable; notes: string[] } {
  const t = modelTexts().costs;
  const e = costEstimate(doc, base);
  const rows: string[][] = e.lines.map((l) => [
    t.items[l.id],
    formatCostQuantity(l.quantity, l.unit),
    `${formatNumber(l.price, 0)} ${t.perUnit[l.unit]}`,
    formatEuro(l.total),
  ]);
  rows.push([t.measured, '', '', formatEuro(e.measured)]);
  for (const s of e.surcharges)
    if (s.percent > 0)
      rows.push([
        fillText(t.surcharge, { label: t.surcharges[s.id], percent: formatNumber(s.percent, 1) }),
        '',
        '',
        formatEuro(s.amount),
      ]);
  rows.push([
    fillText(t.range, { percent: formatNumber(e.range, 1) }),
    '',
    '',
    `${formatEuro(e.low)} – ${formatEuro(e.high)}`,
  ]);
  return {
    table: {
      title: t.title,
      columns: t.columns,
      widths: [3.4, 1.3, 1.4, 1.9],
      numeric: [1, 2, 3],
      rows,
      footer: [t.total, '', '', formatEuro(e.total)],
    },
    notes: [...costDetails(doc, base), ...t.notes],
  };
}

/**
 * The price basis, how the steel and concrete quantities break down, and prices that no
 * longer apply.
 */
export function costDetails(doc: ProjectDocument, base?: PriceBase): string[] {
  const t = modelTexts().costs;
  const one = (v: number) => formatNumber(v, 1, 1);
  const steel = steelTonnage(doc, base);
  const concrete = concreteQuantities(doc);
  const out: string[] = [];
  if (base?.name) out.push(fillText(t.priceBasis, { name: base.name }));
  if (steel.total > 0)
    out.push(
      fillText(t.steelDetail, {
        frame: one(steel.frame),
        secondary: one(steel.secondary),
        bracing: one(steel.bracing),
        columns: one(steel.columns),
        extra: one(steel.extra),
      }),
    );
  if (concrete.volume > 0) {
    const v = (p: ConcretePart) => one(concrete.parts[p].volume);
    out.push(
      fillText(t.concreteDetail, {
        slab: v('slab'),
        beam: v('beam'),
        column: v('column'),
        wall: v('wall'),
        foundation: v('foundation'),
        rebar: one(concrete.rebar),
      }),
    );
  }
  const precast = precastVolume(doc);
  if (precast > 0) out.push(fillText(t.precastDetail, { volume: one(precast) }));
  const prices = doc.settings.costs?.prices ?? {};
  if (REPLACED_ITEMS.some((id) => id in prices)) out.push(t.replacedPrices);
  return out;
}

/**
 * Rough cost estimate: measured quantities times the project's unit prices, the surcharges
 * for what the plan does not show (foundations, building services, fees) and a ± range.
 */
export function costEstimate(doc: ProjectDocument, base?: PriceBase): CostEstimate {
  const quantities = costQuantities(doc, base);
  const lines = COST_ITEMS.flatMap((item): CostLine[] => {
    const quantity = quantities[item.id];
    if (quantity < 1e-6) return [];
    const price = costPrice(doc, item.id, base);
    return [{ id: item.id, unit: item.unit, quantity, price, total: quantity * price }];
  });
  const measured = lines.reduce((s, l) => s + l.total, 0);
  const cfg = doc.settings.costs ?? {};
  const surcharges = COST_SURCHARGES.map((id) => {
    // Footings worked out from the columns replace the percent for foundations.
    const measuredInstead = id === 'foundations' && cfg.foundationsFromColumns === true;
    const percent = measuredInstead ? 0 : surchargePercent(doc, id, base);
    return { id, percent, amount: (measured * percent) / 100, measured: measuredInstead };
  });
  const total = measured + surcharges.reduce((s, x) => s + x.amount, 0);
  const range = surchargePercent(doc, 'range', base);
  return {
    lines,
    steel: steelTonnage(doc, base),
    measured,
    surcharges,
    total,
    low: total * (1 - range / 100),
    high: total * (1 + range / 100),
    range,
  };
}
